import { ChangeEvent, FC, useContext, useEffect, useMemo } from "react";

import {
	getSurfaceBorderColor,
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import { useTime } from "@/app/application/TimeContext";
import { GetLanguageValue, Translate } from "@/app/application/Translate";
import { TranslationContext } from "@/app/providers";
import { IActiveArchiveProcedures } from "@/features/archive/ui/ActiveArchiveProcedures";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import {
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	TextField,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { QuickRangeToggle } from "@/components/date-range/QuickRangeToggle";
import {
	CanonicalQuickRangeKey,
	getMatchingQuickRange,
	shiftDateByQuickRange,
} from "@/components/date-range/quickRange";

import { initialDate } from "@/service/ApplicationQueries";
import { useFetchArchiveUsers } from "@/service/ArchiveQueries";

dayjs.extend(utc);

interface ISearchArchive extends IActiveArchiveProcedures {
	SetStartDate: (date: Date) => void;
	SetEndDate: (date: Date) => void;
	SetSelectedUser: (userId: number) => void;
	/** Set both dates in one update (used by Quick range so table updates correctly) */
	SetDateRange?: (start: Date, end: Date) => void;
}

export const ArchiveProcedureSearch: FC<ISearchArchive> = (props) => {
	const { data: archiveUsers, isLoading: usersLoading } =
		useFetchArchiveUsers();
	const theme = useTheme();
	const { timezone } = useTime();
	const translationContext = useContext(TranslationContext);

	const selectedUserFound = archiveUsers?.Users.findIndex(
		(user) => user.UserId === props.selectedUser,
	);

	// Reset to "All users" when current selection is not in the loaded list (avoid setState during render → infinite loop)
	useEffect(() => {
		if (
			archiveUsers?.Users &&
			selectedUserFound === -1 &&
			props.selectedUser !== 0
		) {
			props.SetSelectedUser(0);
		}
	}, [
		archiveUsers?.Users,
		selectedUserFound,
		props.selectedUser,
		props.SetSelectedUser,
	]);

	const onDateChange = (
		e: ChangeEvent<HTMLInputElement>,
		setDate: (date: Date) => void,
	) => {
		setDate(dayjs.utc(e.target.value, "YYYY-MM-DD").tz(timezone).toDate());
	};

	const onUserChange = (userId: number) => {
		props.SetSelectedUser(userId);
	};

	const setRange = (range: CanonicalQuickRangeKey) => {
		const end = dayjs.utc(initialDate()).tz(timezone).toDate();
		const start = shiftDateByQuickRange(range, end);
		if (props.SetDateRange) {
			props.SetDateRange(start, end);
		} else {
			props.SetStartDate(start);
			props.SetEndDate(end);
		}
	};

	const resetFilters = () => {
		props.SetStartDate(dayjs.utc(initialDate(-14)).tz(timezone).toDate());
		props.SetEndDate(dayjs.utc(initialDate()).tz(timezone).toDate());
		props.SetSelectedUser(0);
	};

	const activeQuickRange = useMemo(() => {
		return getMatchingQuickRange(props.startDate, props.endDate, [
			"3m",
			"6m",
			"1y",
		]);
	}, [props.startDate, props.endDate]);

	const handleQuickRangeChange = (newValue: CanonicalQuickRangeKey) => {
		setRange(newValue);
	};

	return (
		<Box
			sx={{
				width: "100%",
				minWidth: 0,
			}}
		>
			<Stack
				direction={{ xs: "column", lg: "row" }}
				spacing={1.25}
				alignItems={{ xs: "stretch", lg: "center" }}
				flexWrap="wrap"
				useFlexGap
			>
				{/* Keep the controls flat so the filter card is the only visual frame. */}
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={1.25}
					alignItems={{ xs: "stretch", sm: "center" }}
					flexWrap="wrap"
					useFlexGap
				>
					<TextField
						id="history-startdate"
						type="date"
						label={
							<Translate String="Archive.CompletedProcedures.Search.StartDate">
								Date from
							</Translate>
						}
						size="small"
						sx={{
							minWidth: { xs: "100%", sm: 170 },
							flex: { sm: "1 1 170px" },
							"& .MuiOutlinedInput-root": {
								minHeight: 44,
								borderRadius: 2,
								backgroundColor: theme.palette.background.paper,
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: getSurfaceBorderColor(theme, "strong"),
								},
								"&:hover .MuiOutlinedInput-notchedOutline": {
									borderColor: getSurfaceBorderColor(theme, "strong"),
								},
							},
						}}
						value={dayjs.utc(props.startDate).tz(timezone).format("YYYY-MM-DD")}
						onChange={(e) =>
							onDateChange(
								e as ChangeEvent<HTMLInputElement>,
								props.SetStartDate,
							)
						}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						id="history-enddate"
						type="date"
						label={
							<Translate String="Archive.CompletedProcedures.Search.EndDate">
								Date to
							</Translate>
						}
						size="small"
						sx={{
							minWidth: { xs: "100%", sm: 170 },
							flex: { sm: "1 1 170px" },
							"& .MuiOutlinedInput-root": {
								minHeight: 44,
								borderRadius: 2,
								backgroundColor: theme.palette.background.paper,
								"& .MuiOutlinedInput-notchedOutline": {
									borderColor: getSurfaceBorderColor(theme, "strong"),
								},
								"&:hover .MuiOutlinedInput-notchedOutline": {
									borderColor: getSurfaceBorderColor(theme, "strong"),
								},
							},
						}}
						value={dayjs.utc(props.endDate).tz(timezone).format("YYYY-MM-DD")}
						onChange={(e) =>
							onDateChange(e as ChangeEvent<HTMLInputElement>, props.SetEndDate)
						}
						InputLabelProps={{ shrink: true }}
					/>
				</Stack>

				{/* User filter */}
				<FormControl
					size="small"
					sx={{
						minWidth: { xs: "100%", sm: 220 },
						flex: { lg: "0 1 220px" },
						"& .MuiOutlinedInput-root": {
							minHeight: 44,
							borderRadius: 2,
							backgroundColor: theme.palette.background.paper,
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: getSurfaceBorderColor(theme, "strong"),
							},
							"&:hover .MuiOutlinedInput-notchedOutline": {
								borderColor: getSurfaceBorderColor(theme, "strong"),
							},
						},
					}}
				>
					<InputLabel id="history-user-label">
						{
							GetLanguageValue(
								translationContext,
								"Archive.CompletedProcedures.Search.ResponsibleUser.AllUsers",
								"All users",
							).value
						}
					</InputLabel>
					{usersLoading ? (
						<Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
					) : (
						<Select
							labelId="history-user-label"
							label={
								GetLanguageValue(
									translationContext,
									"Archive.CompletedProcedures.Search.ResponsibleUser.AllUsers",
									"All users",
								).value
							}
							value={props.selectedUser}
							onChange={(e) => onUserChange(Number(e.target.value))}
						>
							<MenuItem value={0}>
								{
									GetLanguageValue(
										translationContext,
										"Archive.CompletedProcedures.Search.ResponsibleUser.AllUsers",
										"All users",
									).value
								}
							</MenuItem>
							{archiveUsers?.Users.map((user) => (
								<MenuItem key={user.UserId} value={user.UserId}>
									{user.UserName}
								</MenuItem>
							))}
						</Select>
					)}
				</FormControl>

				{/* Quick range presets – value reflects current range; selected button clearly highlighted */}
				<Stack
					direction={{ xs: "column", sm: "row" }}
					alignItems={{ xs: "stretch", sm: "center" }}
					spacing={0.75}
					sx={{
						flex: "1 1 260px",
						px: 1.25,
						py: 0.75,
						borderRadius: 2,
						border: activeQuickRange
							? `1px solid ${alpha(theme.palette.primary.main, 0.28)}`
							: getSurfaceBorderStyle(theme, "strong"),
						backgroundColor: alpha(
							activeQuickRange
								? theme.palette.primary.main
								: theme.palette.background.default,
							activeQuickRange ? 0.05 : theme.palette.mode === "dark" ? 0.45 : 0.8,
						),
					}}
				>
					<Typography
						variant="caption"
						sx={{
							color: activeQuickRange ? "primary.main" : "text.secondary",
							fontWeight: activeQuickRange ? 700 : 500,
							mr: 0.5,
							whiteSpace: "nowrap",
						}}
					>
						<Translate String="Archive.History.QuickRange">
							Quick range
						</Translate>
						:
					</Typography>
					<QuickRangeToggle
						value={activeQuickRange}
						onChange={handleQuickRangeChange}
						options={[
							{ value: "3m", label: "3m", ariaLabel: "Last 3 months" },
							{ value: "6m", label: "6m", ariaLabel: "Last 6 months" },
							{ value: "1y", label: "1y", ariaLabel: "Last 12 months" },
						]}
						ariaLabel="Archive procedure quick range"
						groupProps={{
							sx: {
								"& .MuiToggleButton-root": {
									minHeight: 40,
									py: 0.5,
									px: 1.25,
									borderRadius: 1.5,
									borderColor: `${getSurfaceBorderColor(theme, "strong")} !important`,
									backgroundColor: "background.paper",
									fontWeight: 800,
								},
								"& .MuiToggleButton-root.Mui-selected": {
									backgroundColor: alpha(theme.palette.primary.main, 0.1),
									color: theme.palette.primary.main,
									fontWeight: 700,
									"&:hover": {
										backgroundColor: alpha(theme.palette.primary.main, 0.14),
									},
								},
							},
						}}
					/>
				</Stack>

				{/* Reset filters */}
				<ProcedureButton
					intent="secondary"
					size="small"
					onClick={resetFilters}
					sx={{
						minHeight: 44,
						px: 1.5,
						width: { xs: "100%", lg: "auto" },
						flexShrink: 0,
						boxShadow: "none",
					}}
				>
					<Translate String="Archive.History.ResetFilters">
						Reset filters
					</Translate>
				</ProcedureButton>
			</Stack>
		</Box>
	);
};
